import { useState, useMemo, useEffect } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { useOpsQueue, QueueFilter } from '@/hooks/useOpsQueue';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, 
  Clock, 
  Building2, 
  Calendar,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  FileText,
  CheckCircle2,
  XCircle,
  Send,
  Package,
  User
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  AuthorizationRequest,
  STATUS_LABELS, 
  STATUS_COLORS, 
  URGENCY_COLORS,
  AuthorizationStatus 
} from '@/lib/authorization/types';
import { formatDistanceToNow, format, parseISO, differenceInHours } from 'date-fns';
import { CaseDetailDrawer } from '@/components/ops/CaseDetailDrawer';

// Queue tab definitions
const QUEUE_TABS = [
  { id: 'active', label: 'Active', icon: Clock, statuses: ['surgeon_approved', 'packet_generated', 'submitted_to_payer', 'payer_pending', 'more_info_requested', 'appeal_in_progress'] },
  { id: 'pending_packet', label: 'Needs Packet', icon: Package, statuses: ['surgeon_approved'] },
  { id: 'awaiting_response', label: 'Awaiting Response', icon: Send, statuses: ['submitted_to_payer', 'payer_pending'] },
  { id: 'action_required', label: 'Action Required', icon: AlertTriangle, statuses: ['more_info_requested'] },
  { id: 'completed', label: 'Completed', icon: CheckCircle2, statuses: ['payer_approved', 'payer_denied', 'resolved', 'closed'] },
];

interface Payer {
  id: string;
  name: string;
}

interface Facility {
  id: string;
  name: string;
}

interface Surgeon {
  id: string;
  full_name: string;
}

export default function OpsQueue() {
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>({ type: 'all' });
  
  // Filter options
  const [payers, setPayers] = useState<Payer[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [surgeons, setSurgeons] = useState<Surgeon[]>([]);
  const [selectedPayerId, setSelectedPayerId] = useState<string>('all');
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('all');
  const [selectedSurgeonId, setSelectedSurgeonId] = useState<string>('all');

  const currentTab = QUEUE_TABS.find(t => t.id === activeTab) || QUEUE_TABS[0];
  
  const { cases, loading, refetch } = useOpsQueue({
    filter: queueFilter,
    statusFilter: currentTab.statuses,
  });

  // Load filter options
  useEffect(() => {
    async function loadFilters() {
      const [payersRes, facilitiesRes, surgeonsRes] = await Promise.all([
        supabase.from('payers').select('id, name').order('name'),
        supabase.from('facilities').select('id, name').order('name'),
        supabase.from('profiles').select('user_id, full_name').order('full_name'),
      ]);
      setPayers(payersRes.data || []);
      setFacilities(facilitiesRes.data || []);
      setSurgeons((surgeonsRes.data || []).map(s => ({ id: s.user_id, full_name: s.full_name })));
    }
    loadFilters();
  }, []);

  // Apply filters
  useEffect(() => {
    if (selectedPayerId !== 'all') {
      setQueueFilter({ type: 'payer', payerId: selectedPayerId });
    } else if (selectedFacilityId !== 'all') {
      setQueueFilter({ type: 'facility', facilityId: selectedFacilityId });
    } else if (selectedSurgeonId !== 'all') {
      setQueueFilter({ type: 'surgeon', surgeonId: selectedSurgeonId });
    } else {
      setQueueFilter({ type: 'all' });
    }
  }, [selectedPayerId, selectedFacilityId, selectedSurgeonId]);

  // Filter by search query
  const filteredCases = useMemo(() => {
    if (!searchQuery) return cases;
    const query = searchQuery.toLowerCase();
    return cases.filter(c => 
      c.case_number.toLowerCase().includes(query) ||
      c.patient?.last_name?.toLowerCase().includes(query) ||
      c.patient?.first_name?.toLowerCase().includes(query) ||
      c.patient?.mrn?.toLowerCase().includes(query) ||
      c.cpt_codes.some(cpt => cpt.includes(query))
    );
  }, [cases, searchQuery]);

  // Calculate SLA status
  const getSlaStatus = (caseData: AuthorizationRequest) => {
    if (!caseData.sla_deadline) return null;
    const hoursRemaining = differenceInHours(parseISO(caseData.sla_deadline), new Date());
    if (hoursRemaining < 0) return 'overdue';
    if (hoursRemaining < 24) return 'critical';
    if (hoursRemaining < 72) return 'warning';
    return 'ok';
  };

  // Count cases per tab for badges
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    QUEUE_TABS.forEach(tab => {
      counts[tab.id] = cases.filter(c => tab.statuses.includes(c.status)).length;
    });
    return counts;
  }, [cases]);

  // Summary stats
  const stats = useMemo(() => {
    const active = cases.filter(c => !['payer_approved', 'payer_denied', 'resolved', 'closed'].includes(c.status));
    const overdue = active.filter(c => getSlaStatus(c) === 'overdue').length;
    const critical = active.filter(c => getSlaStatus(c) === 'critical').length;
    const approved = cases.filter(c => c.status === 'payer_approved').length;
    const denied = cases.filter(c => c.status === 'payer_denied').length;
    return { active: active.length, overdue, critical, approved, denied };
  }, [cases]);

  return (
    <OpsLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Authorization Queue</h1>
              <p className="text-sm text-muted-foreground">
                {stats.active} active • {stats.overdue > 0 && <span className="text-destructive">{stats.overdue} overdue</span>}
                {stats.critical > 0 && <span className="text-orange-500 ml-2">{stats.critical} critical</span>}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <Package className="w-5 h-5 text-cyan-500" />
                <div>
                  <p className="text-xl font-bold">{tabCounts.pending_packet || 0}</p>
                  <p className="text-xs text-muted-foreground">Needs Packet</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <Send className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="text-xl font-bold">{tabCounts.awaiting_response || 0}</p>
                  <p className="text-xs text-muted-foreground">Awaiting Payer</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-xl font-bold">{tabCounts.action_required || 0}</p>
                  <p className="text-xs text-muted-foreground">Action Required</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-xl font-bold">{stats.approved}</p>
                  <p className="text-xs text-muted-foreground">Approved</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-xl font-bold">{stats.denied}</p>
                  <p className="text-xs text-muted-foreground">Denied</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by case #, patient, MRN, or CPT..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedPayerId} onValueChange={(v) => { setSelectedPayerId(v); setSelectedFacilityId('all'); setSelectedSurgeonId('all'); }}>
              <SelectTrigger className="w-[160px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Payer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payers</SelectItem>
                {payers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedFacilityId} onValueChange={(v) => { setSelectedFacilityId(v); setSelectedPayerId('all'); setSelectedSurgeonId('all'); }}>
              <SelectTrigger className="w-[160px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Facility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Facilities</SelectItem>
                {facilities.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedSurgeonId} onValueChange={(v) => { setSelectedSurgeonId(v); setSelectedPayerId('all'); setSelectedFacilityId('all'); }}>
              <SelectTrigger className="w-[160px]">
                <User className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Surgeon" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Surgeons</SelectItem>
                {surgeons.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full max-w-3xl">
              {QUEUE_TABS.map((tab) => {
                const Icon = tab.icon;
                const count = tabCounts[tab.id] || 0;
                return (
                  <TabsTrigger key={tab.id} value={tab.id} className="text-xs sm:text-sm gap-1">
                    <Icon className="w-4 h-4 hidden sm:block" />
                    {tab.label}
                    {count > 0 && (
                      <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">
                        {count}
                      </Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>

        {/* Queue List */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-pulse text-muted-foreground">Loading cases...</div>
            </div>
          ) : filteredCases.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg">No Cases Found</h3>
              <p className="text-muted-foreground mt-1">
                {searchQuery ? 'Try adjusting your search criteria' : 'No cases match the current filters'}
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredCases.map((caseData) => {
                const slaStatus = getSlaStatus(caseData);
                
                return (
                  <Card
                    key={caseData.id}
                    className={cn(
                      "cursor-pointer transition-all hover:border-primary/50",
                      selectedCaseId === caseData.id && "border-primary bg-primary/5",
                      slaStatus === 'overdue' && "border-destructive/50 bg-destructive/5",
                      slaStatus === 'critical' && "border-orange-500/50 bg-orange-500/5"
                    )}
                    onClick={() => setSelectedCaseId(caseData.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Patient & Procedure Info */}
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-medium text-primary">
                              {caseData.case_number}
                            </span>
                            <Badge className={cn('text-xs', STATUS_COLORS[caseData.status as AuthorizationStatus])}>
                              {STATUS_LABELS[caseData.status as AuthorizationStatus]}
                            </Badge>
                            <Badge className={cn('text-xs', URGENCY_COLORS[caseData.urgency])}>
                              {caseData.urgency}
                            </Badge>
                            {slaStatus && slaStatus !== 'ok' && (
                              <Badge 
                                variant="destructive" 
                                className={cn(
                                  "text-xs",
                                  slaStatus === 'warning' && "bg-orange-500"
                                )}
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {slaStatus === 'overdue' ? 'SLA Overdue' : slaStatus === 'critical' ? 'SLA Critical' : 'SLA Warning'}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-medium">
                              {caseData.patient?.last_name}, {caseData.patient?.first_name}
                            </span>
                            <span className="text-muted-foreground">
                              MRN: {caseData.patient?.mrn}
                            </span>
                          </div>

                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {caseData.procedure_description}
                          </p>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {caseData.payer?.name}
                            </span>
                            {caseData.facility && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {caseData.facility.name}
                              </span>
                            )}
                            {caseData.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(parseISO(caseData.scheduled_date), 'MMM d, yyyy')}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDistanceToNow(parseISO(caseData.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>

                        {/* Right: CPT Codes & Arrow */}
                        <div className="flex items-center gap-4">
                          <div className="flex flex-wrap gap-1 justify-end max-w-[150px]">
                            {caseData.cpt_codes.slice(0, 3).map((cpt) => (
                              <Badge key={cpt} variant="outline" className="font-mono text-xs">
                                {cpt}
                              </Badge>
                            ))}
                            {caseData.cpt_codes.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{caseData.cpt_codes.length - 3}
                              </Badge>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Case Detail Drawer */}
      <CaseDetailDrawer
        caseId={selectedCaseId}
        onClose={() => setSelectedCaseId(null)}
      />
    </OpsLayout>
  );
}
